/**
 * Last known device GPS from geofence checks — sent to APIs for server-side validation.
 * Coordinates are not secrets; JWT stays in HttpOnly cookies only.
 */

import { captureSalonDevicePosition, getCurrentDevicePosition } from './deviceGeolocation';

const CACHE_KEY = 'naturals_device_location_cache';

/** Same options as legacy franchiseGeofence.js (pre–data-security refactor). */
export const KIOSK_GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 15000,
};

export function cacheDeviceLocation({ latitude, longitude, accuracy } = {}) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        latitude: lat,
        longitude: lng,
        accuracy: Number.isFinite(Number(accuracy)) ? Number(accuracy) : null,
        saved_at: Date.now(),
      }),
    );
  } catch {
    /* ignore */
  }
}

export function readCachedDeviceLocation() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.latitude);
    const lng = Number(parsed?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      latitude: lat,
      longitude: lng,
      accuracy: Number.isFinite(Number(parsed?.accuracy)) ? Number(parsed.accuracy) : null,
      saved_at: Number(parsed?.saved_at) || 0,
    };
  } catch {
    return null;
  }
}

export function getDeviceLocationHeaders(extra = {}) {
  const cached = readCachedDeviceLocation();
  if (!cached) return { ...extra };
  return {
    ...extra,
    'X-Device-Latitude': String(cached.latitude),
    'X-Device-Longitude': String(cached.longitude),
  };
}

/**
 * Read device GPS using legacy kiosk options. Caches on success.
 * Returns last cached coords if the live read fails (same tolerance as old client geofence).
 */
export async function acquireKioskDeviceLocation(options = {}) {
  const { required = false, forceRefresh = false, ...geoOpts } = options;
  const cached = readCachedDeviceLocation();
  const maxAgeMs =
    Number(geoOpts.maxAgeMs) > 0 ? Number(geoOpts.maxAgeMs) : KIOSK_GEO_OPTIONS.maximumAge;

  if (
    !forceRefresh &&
    cached?.saved_at &&
    Date.now() - cached.saved_at < maxAgeMs
  ) {
    return cached;
  }

  try {
    // Same resilient path as salon login geofence: high-accuracy, then Wi‑Fi / low-accuracy.
    let pos;
    try {
      pos = await captureSalonDevicePosition();
    } catch (firstErr) {
      pos = await getCurrentDevicePosition({
        enableHighAccuracy: geoOpts.enableHighAccuracy ?? KIOSK_GEO_OPTIONS.enableHighAccuracy,
        timeout: geoOpts.timeout ?? KIOSK_GEO_OPTIONS.timeout,
        maximumAge: forceRefresh ? 0 : geoOpts.maximumAge ?? KIOSK_GEO_OPTIONS.maximumAge,
      }).catch(() => {
        throw firstErr;
      });
    }
    cacheDeviceLocation(pos);
    return readCachedDeviceLocation();
  } catch (err) {
    if (required) {
      throw err instanceof Error ? err : new Error('Unable to get your location');
    }
    return cached;
  }
}

/** Non-blocking prefetch on login / capture screens. */
export function prefetchDeviceLocation() {
  void acquireKioskDeviceLocation();
}

/** @deprecated use acquireKioskDeviceLocation */
export async function ensureDeviceLocationCached(options = {}) {
  return acquireKioskDeviceLocation(options);
}

/** Multipart uploads may drop custom headers in some WebViews — duplicate coords in form body. */
export function appendDeviceLocationToFormData(fd) {
  const cached = readCachedDeviceLocation();
  if (!cached || !fd) return false;
  fd.append('device_latitude', String(cached.latitude));
  fd.append('device_longitude', String(cached.longitude));
  return true;
}

export function clearDeviceLocationCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
